import type { Key } from "react";
import type { AdminNavigationSettings } from "./adminNavigationSettingsApi";

export const navigationEditorStorageKey = "mq.navigationEditor.enabled";
export const navigationEditorChangedEvent = "mq:navigation-editor-changed";
const NAVIGATION_EDITOR_ENABLED_VALUE = "true";

export type NavigationItemWithKey = {
  key?: Key | null;
};

export function isNavigationEditorEnabled() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(navigationEditorStorageKey) === NAVIGATION_EDITOR_ENABLED_VALUE;
}

export function setNavigationEditorEnabled(enabled: boolean) {
  if (typeof window === "undefined") return;
  if (enabled) {
    window.localStorage.setItem(navigationEditorStorageKey, NAVIGATION_EDITOR_ENABLED_VALUE);
  } else {
    window.localStorage.removeItem(navigationEditorStorageKey);
  }
  window.dispatchEvent(new Event(navigationEditorChangedEvent));
}

export function getNavigationScopeOrder(settings: AdminNavigationSettings | null | undefined, scopeId: string) {
  return settings?.scopes?.[scopeId]?.order ?? [];
}

export function reorderNavigationItems<TItem extends NavigationItemWithKey>(
  items: TItem[],
  order: readonly string[],
): TItem[] {
  if (order.length === 0) return items;

  const itemByKey = new Map(items.map((item) => [String(item.key ?? ""), item]));
  const usedKeys = new Set<string>();
  const ordered: TItem[] = [];
  for (const key of order) {
    if (usedKeys.has(key)) continue;
    const item = itemByKey.get(key);
    if (!item) continue;
    ordered.push(item);
    usedKeys.add(key);
  }

  return [
    ...ordered,
    ...items.filter((item) => !usedKeys.has(String(item.key ?? ""))),
  ];
}

export function createNavigationSettingsWithScopeOrder(
  settings: AdminNavigationSettings | null | undefined,
  scopeId: string,
  order: string[],
): AdminNavigationSettings {
  return {
    scopes: {
      ...(settings?.scopes ?? {}),
      [scopeId]: {
        order: order.filter(Boolean),
      },
    },
  };
}

export function createNavigationSettingsWithoutScope(
  settings: AdminNavigationSettings | null | undefined,
  scopeId: string,
): AdminNavigationSettings {
  const scopes = { ...(settings?.scopes ?? {}) };
  delete scopes[scopeId];
  return { scopes };
}
